import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle, ArrowRightLeft, CheckCircle2, Clock, Hand, Inbox, Link2, Mail, MessageSquare, Mic,
  Paperclip, Phone, RotateCcw, Search, Send, Smile, Square, StickyNote, Trash2, X,
} from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { Avatar } from '@/components/ui/Avatar'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import { useSupportRealtime } from '@/hooks/useSupportRealtime'
import { SupportSettingsService } from '@/services/modules/SupportSettingsService'
import {
  SupportConversationsService as Service,
  type CannedResponse, type ConversationCard, type ConversationThread,
  type QueueCounters, type QueueScope, type SubscriberOption, type TimelineItem, type WhatsAppTemplate,
} from '@/services/modules/SupportConversationsService'

/** Recarrega a fila quando nada acontece — rede de segurança do WebSocket. */
const FALLBACK_POLL_MS = 30_000

/**
 * Filtros da fila. O rótulo é curto de propósito: na coluna de 320px os nomes
 * por extenso quebravam em duas linhas e pareciam defeito. O nome completo fica
 * no `title` para quem passar o mouse.
 */
const SCOPES: Array<{ id: QueueScope; counterKey: keyof QueueCounters; label: string; title: string }> = [
  { id: 'minhas',         counterKey: 'minhas',        label: 'Minhas', title: 'Chamados atribuídos a mim' },
  { id: 'nao_atribuidas', counterKey: 'naoAtribuidas', label: 'Livres', title: 'Chamados ainda sem atendente' },
  { id: 'equipe',         counterKey: 'equipe',        label: 'Equipe', title: 'Chamados das minhas filas' },
  { id: 'todas',          counterKey: 'todas',         label: 'Todas',  title: 'Todos os chamados que posso ver' },
]

const STATUS_VARIANT: Record<string, 'amber' | 'green' | 'gray' | 'blue'> = {
  ABERTO: 'amber', PENDENTE: 'amber', ADIADO: 'blue', ENCERRADO: 'green',
}

/**
 * Formato do áudio gravado. A ordem importa: os primeiros são os que a Meta
 * aceita como mensagem de voz no WhatsApp. O MP4 precisa pedir AAC
 * (`mp4a.40.2`) com todas as letras: o `audio/mp4` puro do Chrome grava Opus,
 * que a Meta recusa. O webm entra por último — aí o arquivo segue como documento.
 */
const AUDIO_MIMES = ['audio/ogg;codecs=opus', 'audio/mp4;codecs=mp4a.40.2', 'audio/mpeg', 'audio/webm;codecs=opus', 'audio/webm']

/** Limite de gravação: acima disso o arquivo fica grande e o cliente não ouve. */
const AUDIO_MAX_SECONDS = 300

/** Teto da mensagem no servidor. O contador aparece perto do fim, não antes. */
const MESSAGE_MAX = 4000
const MESSAGE_WARN = 3500

/** Emojis mais usados no atendimento — evita dependência externa. */
const EMOJIS = ['🙂', '😀', '😉', '👍', '🙏', '✅', '❌', '⚠️', '📎', '📄', '💳', '📅', '⏰', '❤️', '🎉', '😕']

export function SupportInboxPage() {
  const { can } = useAuthority()
  const canReply = can('support.chat.reply')
  const canNote = can('support.chat.notes')
  const canAssign = can('support.chat.assign')
  const canTransfer = can('support.chat.transfer')
  const canResolve = can('support.chat.resolve')

  const [scope, setScope] = useState<QueueScope>('minhas')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<ConversationCard[]>([])
  const [counters, setCounters] = useState<QueueCounters>({ minhas: 0, naoAtribuidas: 0, equipe: 0, todas: 0 })
  const [loadingQueue, setLoadingQueue] = useState(true)

  const [selected, setSelected] = useState<string | null>(null)
  const [thread, setThread] = useState<ConversationThread | null>(null)
  const [loadingThread, setLoadingThread] = useState(false)

  const [draft, setDraft] = useState('')
  const [isNote, setIsNote] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [sending, setSending] = useState(false)
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [canned, setCanned] = useState<CannedResponse[]>([])

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([])
  const [templateId, setTemplateId] = useState('')
  const [templateVars, setTemplateVars] = useState<string[]>([])

  // Gravação de áudio (MediaRecorder do próprio navegador).
  const [recording, setRecording] = useState(false)
  const [recordedSeconds, setRecordedSeconds] = useState(0)
  const recorder = useRef<MediaRecorder | null>(null)
  const recorderTimer = useRef<number | null>(null)

  // Vínculo do contato a um assinante.
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkSearch, setLinkSearch] = useState('')
  const [linkRows, setLinkRows] = useState<SubscriberOption[]>([])
  const [linkLoading, setLinkLoading] = useState(false)
  const [linking, setLinking] = useState(false)

  const [transferOpen, setTransferOpen] = useState(false)
  const [transferUser, setTransferUser] = useState('')
  const [transferReason, setTransferReason] = useState('')
  const [agents, setAgents] = useState<Array<{ value: string; label: string }>>([])

  const fileInput = useRef<HTMLInputElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // -- Fila ---------------------------------------------------------------
  const loadQueue = useCallback(async (quiet = false) => {
    if (!quiet) setLoadingQueue(true)
    try {
      const res = await Service.list({ scope, search })
      setRows(res.data)
      setCounters(res.counters)
    } catch (e) {
      toast.error({ title: 'Erro na fila', description: extractApiError(e).message })
    } finally {
      setLoadingQueue(false)
    }
  }, [scope, search])

  useEffect(() => { void loadQueue() }, [loadQueue])

  // Fallback: se o WebSocket cair, a fila ainda se atualiza sozinha.
  useEffect(() => {
    const t = window.setInterval(() => void loadQueue(true), FALLBACK_POLL_MS)
    return () => window.clearInterval(t)
  }, [loadQueue])

  // -- Thread -------------------------------------------------------------
  const loadThread = useCallback(async (id: string, quiet = false) => {
    if (!quiet) setLoadingThread(true)
    try {
      setThread(await Service.thread(id))
    } catch (e) {
      toast.error({ title: 'Erro ao abrir', description: extractApiError(e).message })
    } finally {
      setLoadingThread(false)
    }
  }, [])

  useEffect(() => {
    if (selected) void loadThread(selected)
    else setThread(null)
  }, [selected, loadThread])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [thread?.timeline.length])

  useEffect(() => {
    Service.cannedResponses(selected ?? undefined).then(setCanned).catch(() => setCanned([]))
  }, [selected])

  useEffect(() => {
    Service.templates().then(setTemplates).catch(() => setTemplates([]))
  }, [])

  useEffect(() => {
    SupportSettingsService.agents()
      .then((rows) => setAgents(rows.map((u) => ({ value: u.id, label: u.name }))))
      .catch(() => setAgents([]))
  }, [])

  // Busca de assinante para vincular — só a partir de 3 letras, como no backend.
  useEffect(() => {
    if (!linkOpen) return
    const termo = linkSearch.trim()
    if (termo.length < 3) { return }
    const t = window.setTimeout(() => {
      setLinkLoading(true)
      Service.subscriberSearch(termo)
        .then(setLinkRows)
        .catch(() => setLinkRows([]))
        .finally(() => setLinkLoading(false))
    }, 350)
    return () => window.clearTimeout(t)
  }, [linkOpen, linkSearch])

  // Tempo real: mensagem nova e mudança de dono chegam sem recarregar.
  useSupportRealtime(selected, {
    onMessage: () => { if (selected) void loadThread(selected, true); void loadQueue(true) },
    onConversationUpdated: () => { if (selected) void loadThread(selected, true); void loadQueue(true) },
    // Só troca o status da bolha: não precisa recarregar a conversa inteira.
    onMessageStatus: (e) => {
      const { messageId, status, failed } = e as { messageId: string; status: string | null; failed: string | null }
      setThread((t) => t && {
        ...t,
        timeline: t.timeline.map((i) => (i.kind === 'mensagem' && i.id === messageId ? { ...i, status, failed } : i)),
      })
    },
  })

  // -- Ações --------------------------------------------------------------
  async function send() {
    const text = draft.trim()
    if (!selected || (!text && files.length === 0)) return

    setSending(true)
    try {
      isNote
        ? await Service.note(selected, text, files)
        : await Service.reply(selected, text, files)
      setDraft(''); setFiles([])
      await loadThread(selected, true)
      void loadQueue(true)
    } catch (e) {
      toast.error({ title: 'Não foi possível enviar', description: extractApiError(e).message })
    } finally { setSending(false) }
  }

  /**
   * Enter envia, Shift+Enter quebra a linha — o que todo mundo espera de um
   * chat. Sem isso o atendente precisava tirar a mão do teclado a cada resposta.
   */
  function onDraftKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter' || e.shiftKey) return
    // Enquanto o teclado monta um acento (IME), Enter é confirmação, não envio.
    if (e.nativeEvent.isComposing) return
    e.preventDefault()
    if (sending || (!draft.trim() && files.length === 0)) return
    void send()
  }

  /** Print colado (Ctrl+V) vira anexo — é assim que a equipe manda a tela do cliente. */
  function onDraftPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const colados = Array.from(e.clipboardData.files)
    if (colados.length === 0) return
    e.preventDefault()
    const agora = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    setFiles((p) => [...p, ...colados.map((f, i) => (
      f.name && f.name !== 'image.png'
        ? f
        : new File([f], `print-${agora}${colados.length > 1 ? `-${i + 1}` : ''}.png`, { type: f.type })
    ))])
  }

  async function startRecording() {
    if (recording) return
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error({
        title: 'Gravação indisponível',
        description: 'Este navegador não grava áudio. Use o Chrome, o Edge ou o Firefox atualizados.',
      })
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mime = AUDIO_MIMES.find((m) => MediaRecorder.isTypeSupported(m))
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      const pedacos: BlobPart[] = []

      rec.ondataavailable = (ev) => { if (ev.data.size > 0) pedacos.push(ev.data) }
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        if (recorderTimer.current) { window.clearInterval(recorderTimer.current); recorderTimer.current = null }
        setRecording(false)
        setRecordedSeconds(0)
        // Cancelado: o áudio é descartado sem virar anexo.
        if (rec.mimeType === 'descartado' || pedacos.length === 0) return
        const tipo = rec.mimeType || mime || 'audio/webm'
        const blob = new Blob(pedacos, { type: tipo })
        const ext = tipo.includes('ogg') ? 'ogg' : tipo.includes('mp4') ? 'm4a' : tipo.includes('mpeg') ? 'mp3' : 'webm'
        const nome = `audio-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${ext}`
        setFiles((p) => [...p, new File([blob], nome, { type: tipo })])
      }

      recorder.current = rec
      rec.start()
      setRecording(true)
      setRecordedSeconds(0)
      recorderTimer.current = window.setInterval(() => {
        setRecordedSeconds((sec) => {
          if (sec + 1 >= AUDIO_MAX_SECONDS) { recorder.current?.stop() }
          return sec + 1
        })
      }, 1000)
    } catch {
      toast.error({
        title: 'Microfone bloqueado',
        description: 'Autorize o uso do microfone para este site no cadeado ao lado do endereço.',
      })
    }
  }

  function stopRecording(descartar = false) {
    const rec = recorder.current
    if (!rec) return
    if (descartar) {
      // Sinaliza o descarte para o onstop: os pedaços gravados são ignorados.
      rec.ondataavailable = null
      Object.defineProperty(rec, 'mimeType', { value: 'descartado', configurable: true })
    }
    rec.stop()
    recorder.current = null
  }

  // Sair da tela no meio da gravação não pode deixar o microfone aberto.
  useEffect(() => () => { recorder.current?.stream.getTracks().forEach((t) => t.stop()) }, [])

  async function act(fn: () => Promise<unknown>, ok: string, detail = 'Registrado na linha do tempo do chamado.') {
    try {
      await fn()
      toast.success({ title: ok, description: detail })
      if (selected) await loadThread(selected, true)
      await loadQueue(true)
    } catch (e) {
      toast.error({ title: 'Não foi possível concluir', description: extractApiError(e).message })
    }
  }

  const conv = thread?.conversation
  const isResolved = conv?.status === 'ENCERRADO'

  // Janela de 24h do WhatsApp: fechada, a Meta só aceita modelo aprovado. A
  // tela troca o compositor em vez de deixar o atendente escrever e só depois
  // descobrir que não podia.
  const windowClosed = conv?.channel === 'WHATSAPP'
    && (!conv.windowExpiresAt || new Date(conv.windowExpiresAt) <= new Date())
  const template = templates.find((t) => t.id === templateId) ?? null

  /**
   * Marcar como não lida e sair do chamado: continuar com ele aberto faria a
   * própria tela marcar tudo como lido de novo na recarga seguinte.
   */
  async function markUnread() {
    if (!selected) return
    try {
      await Service.markUnread(selected)
      setSelected(null)
      setThread(null)
      await loadQueue(true)
      toast.success({ title: 'Marcado como não lido', description: 'O chamado voltou para a fila com o aviso de nova mensagem.' })
    } catch (e) {
      toast.error({ title: 'Não foi possível marcar', description: extractApiError(e).message })
    }
  }

  async function sendTemplate() {
    if (!selected || !template) return
    setSending(true)
    try {
      await Service.sendTemplate(selected, template.id, templateVars)
      setTemplateId(''); setTemplateVars([])
      await loadThread(selected, true); void loadQueue(true)
    } catch (e) {
      toast.error({ title: 'Não foi possível enviar o modelo', description: extractApiError(e).message })
    } finally { setSending(false) }
  }

  return (
    <>
      <PageHeader
        title="Central de atendimento"
        subtitle="Chamados do aplicativo e do WhatsApp"
        crumbs={[{ label: 'Atendimento' }, { label: 'Central' }]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_minmax(0,1fr)_300px]">
        {/* ------------------------------------------------ FILA */}
        <aside className="flex max-h-[calc(100vh-190px)] flex-col overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
          <div className="space-y-2.5 border-b border-gray-100 p-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, telefone ou conteúdo"
              actionRight={<Search size={14} className="text-gray-400" />}
            />

            {/* Segmentado: cabe numa linha e mostra as quatro contagens juntas. */}
            <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
              {SCOPES.map((sc) => {
                const active = sc.id === scope
                const count = counters[sc.counterKey] ?? 0
                // Fila sem dono é a que exige ação: destaca mesmo sem estar aberta.
                const urgent = sc.id === 'nao_atribuidas' && count > 0
                return (
                  <button
                    key={sc.id}
                    type="button"
                    title={sc.title}
                    onClick={() => setScope(sc.id)}
                    className={`cursor-pointer flex min-w-0 flex-1 flex-col items-center justify-center rounded-md px-1 py-1.5 leading-tight transition ${
                      active ? 'bg-white shadow-sm' : 'hover:bg-white/60'
                    }`}
                  >
                    <span className={`text-[15px] font-semibold ${
                      active ? 'text-brand-700' : urgent ? 'text-amber-600' : 'text-gray-700'
                    }`}>
                      {count}
                    </span>
                    <span className={`w-full break-words text-center text-[11px] ${
                      active ? 'font-medium text-brand-700' : 'text-gray-500'
                    }`}>
                      {sc.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingQueue && (
              <div className="space-y-px">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse border-b border-gray-50 p-3">
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200" />
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="h-3 w-2/3 rounded bg-gray-200" />
                        <div className="h-2.5 w-full rounded bg-gray-100" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loadingQueue && rows.length === 0 && (
              <EmptyState
                icon={<Inbox size={22} />}
                title={search ? 'Nada encontrado' : 'Fila vazia'}
                description={search
                  ? 'Nenhum chamado casa com a busca. Tente outro nome, telefone ou trecho da conversa.'
                  : scope === 'todas'
                    ? 'Não há chamados abertos no momento.'
                    : 'Nenhum chamado neste filtro. Veja em "Livres" ou "Todas".'}
              />
            )}
            {rows.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                className={`relative cursor-pointer flex w-full gap-2.5 border-b border-gray-50 p-3 text-left transition hover:bg-gray-50 ${
                  selected === c.id ? 'bg-brand-50/70' : ''
                }`}
              >
                {/* Marca lateral do item aberto — mais claro que só o fundo. */}
                {selected === c.id && (
                  <span aria-hidden className="absolute inset-y-0 left-0 w-1 rounded-r bg-brand-500" />
                )}

                <div className="relative shrink-0 self-start">
                  <Avatar name={c.contact.name} size="md" />
                  {/* O canal vira um ponto no avatar: informa sem roubar a linha do nome. */}
                  <span
                    title={c.channel === 'WHATSAPP' ? 'WhatsApp' : 'Aplicativo'}
                    className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white ${
                      c.channel === 'WHATSAPP' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className={`min-w-0 flex-1 break-words text-sm ${
                      c.unread > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'
                    }`}>
                      {c.contact.name}
                    </span>
                    <span className="shrink-0 text-[11px] text-gray-400">{c.formatted.lastMessageAt ?? '—'}</span>
                  </div>

                  <p className={`mt-0.5 break-words text-xs ${c.unread > 0 ? 'text-gray-700' : 'text-gray-500'}`}>
                    {c.lastMessage ?? 'Sem mensagens'}
                  </p>

                  <div className="mt-1.5 flex items-center gap-1.5">
                    <Badge variant={STATUS_VARIANT[c.status] ?? 'gray'}>{c.status.toLowerCase()}</Badge>
                    <span className="min-w-0 flex-1 break-words text-[11px] text-gray-400">
                      {c.assignee?.name ?? 'sem dono'}
                    </span>
                    {c.unread > 0 && (
                      <span className="shrink-0 rounded-full bg-brand-500 px-1.5 text-[10px] font-semibold leading-[18px] text-white">
                        {c.unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* ------------------------------------------------ THREAD */}
        <section className="flex max-h-[calc(100vh-190px)] flex-col rounded-lg bg-white ring-1 ring-gray-200">
          {!selected && (
            <div className="flex flex-1 items-center justify-center p-8">
              <EmptyState
                icon={<MessageSquare size={22} />}
                title="Escolha um chamado"
                description="Selecione uma conversa na fila ao lado para começar o atendimento."
              />
            </div>
          )}

          {selected && conv && (
            <>
              <header className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3">
                <div className="relative shrink-0 self-start">
                  <Avatar name={conv.contact.name} size="md" />
                  <span
                    title={conv.channel === 'WHATSAPP' ? 'WhatsApp' : 'Aplicativo'}
                    className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white ${
                      conv.channel === 'WHATSAPP' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="break-words text-sm font-semibold text-gray-900">{conv.contact.name}</p>
                    <Badge variant={STATUS_VARIANT[conv.status] ?? 'gray'}>{conv.status.toLowerCase()}</Badge>
                  </div>
                  <p className="break-words text-[11px] text-gray-500">
                    {conv.inboxName} · {conv.assignee?.name ?? 'sem dono'}
                    {conv.team && ` · ${conv.team.name}`}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {canAssign && !conv.assignee && (
                    <Button size="sm" variant="secondary" iconLeft={<Hand size={13} />}
                      onClick={() => void act(() => Service.assign(conv.id), 'Chamado assumido')}>Assumir</Button>
                  )}
                  {conv.contact.phone && (
                    <a
                      href={`https://wa.me/${conv.contact.phone.replace(/\D/g, '')}`}
                      target="_blank" rel="noreferrer"
                      title="Abrir conversa no WhatsApp"
                      className="hidden items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 sm:inline-flex"
                    >
                      <Phone size={12} /> {conv.contact.phone}
                    </a>
                  )}
                  <Button size="sm" variant="secondary" iconLeft={<Mail size={13} />}
                    title="Devolve o chamado para a fila de não lidos e fecha a conversa"
                    onClick={() => void markUnread()}>Não lida</Button>
                  {canTransfer && !isResolved && (
                    <Button size="sm" variant="secondary" iconLeft={<ArrowRightLeft size={13} />}
                      onClick={() => { setTransferOpen(true); setTransferReason(''); setTransferUser('') }}>Transferir</Button>
                  )}
                  {canResolve && (isResolved
                    ? <Button size="sm" variant="secondary" iconLeft={<RotateCcw size={13} />}
                        onClick={() => void act(() => Service.reopen(conv.id), 'Chamado reaberto')}>Reabrir</Button>
                    : <Button size="sm" iconLeft={<CheckCircle2 size={13} />}
                        onClick={() => void act(() => Service.resolve(conv.id), 'Chamado encerrado')}>Encerrar</Button>
                  )}
                </div>
              </header>

              <div className="flex-1 space-y-3 overflow-y-auto bg-gray-50/40 p-4">
                {loadingThread && (
                  <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className={`flex animate-pulse ${i % 2 ? 'justify-end' : ''}`}>
                        <div className={`h-12 rounded-lg bg-gray-200 ${i % 2 ? 'w-1/2' : 'w-2/3'}`} />
                      </div>
                    ))}
                  </div>
                )}
                {thread?.timeline.map((item) => <TimelineRow key={item.id} item={item} />)}
                <div ref={bottomRef} />
              </div>

              {/* ---------------------------------- COMPOSITOR */}
              {!isResolved && windowClosed ? (
                <div className="border-t border-amber-200 bg-amber-50/60 p-3">
                  <p className="mb-2 text-xs font-medium text-amber-800">
                    A janela de 24 horas fechou. Só é possível enviar um modelo aprovado pela Meta.
                  </p>
                  {templates.length === 0 ? (
                    <p className="text-[11px] text-amber-700">
                      Nenhum modelo aprovado cadastrado. Cadastre um para conseguir retomar a conversa.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <Select
                        value={templateId}
                        onChange={(e) => {
                          setTemplateId(e.target.value)
                          const t = templates.find((x) => x.id === e.target.value)
                          setTemplateVars(Array.from({ length: t?.variablesCount ?? 0 }, () => ''))
                        }}
                        options={templates.map((t) => ({ value: t.id, label: `${t.name} (${t.language})` }))}
                        placeholder="Escolha o modelo"
                        searchPlaceholder="Buscar modelo"
                      />
                      {template?.body && (
                        <p className="rounded bg-white p-2 text-[11px] text-gray-600 ring-1 ring-amber-100">{template.body}</p>
                      )}
                      {templateVars.map((v, i) => (
                        <Input
                          key={i} value={v} placeholder={`Valor da variável {{${i + 1}}}`}
                          onChange={(e) => setTemplateVars((p) => p.map((x, idx) => (idx === i ? e.target.value : x)))}
                        />
                      ))}
                      <Button
                        iconLeft={<Send size={14} />} loading={sending}
                        disabled={!templateId || templateVars.some((v) => !v.trim())}
                        onClick={() => void sendTemplate()}
                      >Enviar modelo</Button>
                    </div>
                  )}
                </div>
              ) : isResolved ? (
                <p className="border-t border-gray-100 p-4 text-center text-xs text-gray-500">
                  Chamado encerrado. Reabra para responder — ou aguarde: se o cliente escrever, um novo chamado é aberto.
                </p>
              ) : (
                <div className={`border-t ${isNote ? 'border-amber-200' : 'border-gray-100'}`}>
                  {/* Modo sempre visível: escrever ao cliente achando que era
                      nota interna é o erro caro deste módulo. */}
                  <div className={`flex items-center gap-2 border-b px-3 py-2 ${
                    isNote ? 'border-amber-200 bg-amber-50/70' : 'border-gray-100 bg-gray-50/70'
                  }`}>
                    <div className="inline-flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
                      <button type="button" onClick={() => setIsNote(false)} disabled={!canReply}
                        aria-pressed={!isNote}
                        className={`cursor-pointer rounded-md px-3 py-1 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                          !isNote ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}>
                        Resposta ao cliente
                      </button>
                      {canNote && (
                        <button type="button" onClick={() => setIsNote(true)} aria-pressed={isNote}
                          className={`cursor-pointer inline-flex items-center gap-1 rounded-md px-3 py-1 text-xs font-semibold transition ${
                            isNote ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}>
                          <StickyNote size={12} /> Nota interna
                        </button>
                      )}
                    </div>
                    <span className={`ml-auto break-words text-[11px] ${isNote ? 'font-medium text-amber-700' : 'text-gray-500'}`}>
                      {isNote
                        ? 'só a equipe vê'
                        : conv.channel === 'WHATSAPP'
                          ? `vai para o WhatsApp de ${conv.contact.name}`
                          : `vai para o aplicativo de ${conv.contact.name}`}
                    </span>
                  </div>

                  <div className={`px-3 pt-3 ${isNote ? 'bg-amber-50/40' : ''}`}>
                    <Textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={onDraftKeyDown}
                      onPaste={onDraftPaste}
                      maxLength={MESSAGE_MAX}
                      placeholder={isNote ? 'Escreva uma observação para a equipe' : 'Escreva a resposta ao cliente'}
                      rows={3}
                      // Corretor ortográfico do navegador, em português: sublinha
                      // o erro e o botão direito mostra as correções. Roda no
                      // próprio navegador — a mensagem não vai para serviço externo.
                      spellCheck
                      lang="pt-BR"
                      autoCorrect="on"
                      autoCapitalize="sentences"
                    />

                    {files.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-start gap-2">
                        {files.map((f, i) => (
                          <PendingFile key={`${f.name}-${i}`} file={f} onRemove={() => setFiles((p) => p.filter((_, idx) => idx !== i))} />
                        ))}
                      </div>
                    )}

                    {recording && (
                      <div className="mt-2 flex items-center gap-2 rounded-md bg-rose-50 px-3 py-1.5 text-xs text-rose-800 ring-1 ring-rose-200">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500" />
                        Gravando <span className="tabular-nums">{formatSeconds(recordedSeconds)}</span>
                        <span className="text-rose-500">máx. {formatSeconds(AUDIO_MAX_SECONDS)}</span>
                        <button type="button" onClick={() => stopRecording(false)}
                          className="cursor-pointer ml-auto inline-flex items-center gap-1 rounded-md bg-rose-600 px-2 py-1 text-white transition hover:bg-rose-700">
                          <Square size={11} /> Parar e anexar
                        </button>
                        <button type="button" onClick={() => stopRecording(true)} title="Descartar gravação"
                          className="cursor-pointer inline-flex items-center gap-1 rounded-md px-2 py-1 text-rose-700 transition hover:bg-rose-100">
                          <Trash2 size={12} /> Descartar
                        </button>
                      </div>
                    )}
                    <div className="h-3" />
                  </div>

                  {/* Barra de ações: ícone agrupado à esquerda, envio à direita.
                      O rótulo vive no title e no leitor de tela. */}
                  <div className={`flex items-center gap-1 border-t px-2 py-1.5 ${isNote ? 'border-amber-200' : 'border-gray-100'}`}>
                    <input
                      ref={fileInput} type="file" multiple hidden
                      onChange={(e) => setFiles((p) => [...p, ...Array.from(e.target.files ?? [])])}
                    />
                    <IconButton label="Anexar arquivo" onClick={() => fileInput.current?.click()}>
                      <Paperclip size={16} />
                    </IconButton>
                    <IconButton
                      label={recording ? 'Parar a gravação' : 'Gravar áudio'}
                      active={recording}
                      onClick={() => (recording ? stopRecording(false) : void startRecording())}
                    >
                      {recording ? <Square size={16} /> : <Mic size={16} />}
                    </IconButton>

                    <div className="relative">
                      <IconButton label="Inserir emoji" active={emojiOpen} onClick={() => setEmojiOpen((v) => !v)}>
                        <Smile size={16} />
                      </IconButton>
                      {emojiOpen && (
                        <div className="absolute bottom-11 left-0 z-10 grid w-56 grid-cols-8 gap-1 rounded-lg bg-white p-2 shadow-lg ring-1 ring-gray-200">
                          {EMOJIS.map((e) => (
                            <button key={e} type="button" className="cursor-pointer rounded p-1 text-lg hover:bg-gray-100"
                              onClick={() => { setDraft((d) => d + e); setEmojiOpen(false) }}>{e}</button>
                          ))}
                        </div>
                      )}
                    </div>

                    {canned.length > 0 && (
                      <>
                        <span aria-hidden className="mx-1 h-4 w-px bg-gray-200" />
                        <div className="w-44">
                          <Select
                            value="" onChange={(e) => {
                              const c = canned.find((x) => x.id === e.target.value)
                              if (c) setDraft((d) => (d ? `${d}\n${c.content}` : c.content))
                            }}
                            options={canned.map((c) => ({ value: c.id, label: `${c.shortcut} — ${c.title}` }))}
                            placeholder="Resposta rápida"
                            searchPlaceholder="Buscar atalho"
                          />
                        </div>
                      </>
                    )}

                    {/* Dica fixa: no texto de apoio ela sumia no primeiro caractere. */}
                    <span className="ml-auto hidden items-center gap-1 text-[11px] text-gray-400 sm:flex">
                      {draft.length >= MESSAGE_WARN ? (
                        <span className={`tabular-nums ${draft.length > MESSAGE_MAX - 200 ? 'font-medium text-amber-600' : ''}`}>
                          {draft.length.toLocaleString('pt-BR')} / {MESSAGE_MAX.toLocaleString('pt-BR')}
                        </span>
                      ) : (
                        <>
                          <Kbd>Enter</Kbd> envia · <Kbd>Shift</Kbd>+<Kbd>Enter</Kbd> quebra · botão direito corrige palavras sublinhadas
                        </>
                      )}
                    </span>

                    <Button
                      className="ml-2"
                      iconLeft={<Send size={14} />}
                      loading={sending}
                      disabled={(!canReply && !isNote) || (!draft.trim() && files.length === 0)}
                      title={draft.trim() || files.length > 0 ? 'Enter também envia' : 'Escreva algo ou anexe um arquivo'}
                      onClick={() => void send()}
                    >
                      {isNote ? 'Comentar' : 'Enviar'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        {/* ------------------------------------------------ CONTEXTO */}
        <aside className="max-h-[calc(100vh-190px)] space-y-4 overflow-y-auto rounded-lg bg-white p-4 ring-1 ring-gray-200">
          {!thread && <p className="text-sm text-gray-500">Sem chamado aberto.</p>}

          {thread?.conversation.contact.phone && (
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Telefone</p>
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-700">{thread.conversation.contact.phone}</p>
                <a href={`https://wa.me/${thread.conversation.contact.phone.replace(/\D/g, '')}`}
                   target="_blank" rel="noreferrer" title="Abrir no WhatsApp"
                   className="text-gray-400 transition hover:text-brand-600">
                  <Phone size={13} />
                </a>
              </div>
            </div>
          )}

          {thread && !thread.context.linked && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-xs font-medium text-amber-800">Contato sem cadastro</p>
              <p className="mt-1 text-[11px] text-amber-700">
                Este número não está ligado a um assinante. Vincule para ver plano, faturas e dependentes.
              </p>
              <Button variant="secondary" iconLeft={<Link2 size={14} />} className="mt-2"
                onClick={() => { setLinkOpen(true); setLinkSearch(''); setLinkRows([]) }}>
                Vincular a um assinante
              </Button>
            </div>
          )}

          {thread?.context.linked && (
            <>
              <div>
                <p className="text-sm font-medium text-gray-800">{thread.context.name}</p>
                <p className="text-[11px] text-gray-500">{thread.context.cpf} · {thread.context.status}</p>
                <button type="button" onClick={() => { setLinkOpen(true); setLinkSearch(''); setLinkRows([]) }}
                  className="cursor-pointer mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400 underline transition hover:text-brand-600">
                  <Link2 size={11} /> Trocar assinante vinculado
                </button>
              </div>
              <Field label="Plano" value={thread.context.plan ?? '—'} />
              <Field label="Carteirinha" value={thread.context.card ?? '—'} />

              {(thread.context.invoices?.length ?? 0) > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Últimas faturas</p>
                  <ul className="space-y-1">
                    {thread.context.invoices!.map((i) => (
                      <li key={i.id} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{i.dueDate}</span>
                        <span className="text-gray-800">{i.amount}</span>
                        <Badge variant={i.status === 'PAGO' ? 'green' : 'amber'}>{i.status.toLowerCase()}</Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(thread.context.dependents?.length ?? 0) > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Dependentes</p>
                  <ul className="space-y-0.5 text-xs text-gray-600">
                    {thread.context.dependents!.map((d) => <li key={d.id}>{d.name}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}

          {(thread?.history.length ?? 0) > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Atendimentos anteriores</p>
              <ul className="space-y-1">
                {thread!.history.map((h) => (
                  <li key={h.id}>
                    <button type="button" onClick={() => setSelected(h.id)}
                      className="cursor-pointer flex w-full items-center justify-between rounded px-1 py-0.5 text-xs hover:bg-gray-50">
                      <span className="text-gray-600">{h.formatted.lastMessageAt ?? '—'}</span>
                      <Badge variant={STATUS_VARIANT[h.status] ?? 'gray'}>{h.status.toLowerCase()}</Badge>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {/* Vincular contato a um assinante — busca por nome, CPF ou telefone. */}
      <Modal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        title="Vincular a um assinante"
        size="md"
        footer={<Button variant="ghost" onClick={() => setLinkOpen(false)}>Fechar</Button>}
      >
        <div className="space-y-3">
          <Input
            value={linkSearch}
            onChange={(e) => setLinkSearch(e.target.value)}
            placeholder="Nome, CPF ou telefone"
            hint="A partir de 3 caracteres. O vínculo vale para todos os chamados deste contato."
            actionRight={<Search size={14} className="text-gray-400" />}
          />

          {linkLoading && <p className="text-xs text-gray-400">Buscando…</p>}
          {!linkLoading && linkSearch.trim().length >= 3 && linkRows.length === 0 && (
            <p className="text-xs text-gray-500">Nenhum assinante encontrado.</p>
          )}

          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {linkRows.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  disabled={linking}
                  onClick={() => {
                    setLinking(true)
                    void act(() => Service.linkContact(selected!, s.id), 'Contato vinculado', `Agora este contato é ${s.name}.`)
                      .finally(() => { setLinking(false); setLinkOpen(false) })
                  }}
                  className="cursor-pointer flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left transition hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="min-w-0">
                    <span className="block break-words text-sm text-gray-800">{s.name}</span>
                    <span className="block break-words text-[11px] text-gray-500">{s.cpf ?? '—'} · {s.phone ?? 'sem telefone'}</span>
                  </span>
                  <Badge variant={s.status === 'ATIVO' ? 'green' : 'gray'}>{s.status.toLowerCase()}</Badge>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Modal>

      {/* Transferência — motivo obrigatório, como no backend. */}
      <Modal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        title="Transferir chamado"
        size="sm"
        footer={<>
          <Button variant="ghost" onClick={() => setTransferOpen(false)}>Cancelar</Button>
          <Button
            disabled={!transferUser || !transferReason.trim()}
            onClick={() => {
              void act(() => Service.transfer(selected!, { userId: transferUser, reason: transferReason }), 'Chamado transferido')
              setTransferOpen(false)
            }}
          >Transferir</Button>
        </>}
      >
        <div className="space-y-3">
          <Select
            label="Para quem" required value={transferUser}
            onChange={(e) => setTransferUser(e.target.value)}
            options={agents} placeholder="Selecione o atendente" searchPlaceholder="Buscar atendente"
          />
          <Input
            label="Motivo" required value={transferReason}
            onChange={(e) => setTransferReason(e.target.value)}
            placeholder="Ex.: assunto de cobrança"
            hint="Fica registrado na linha do tempo do chamado."
          />
        </div>
      </Modal>
    </>
  )
}

/** Uma linha da thread: mensagem, nota interna ou marco do chamado. */
function TimelineRow({ item }: { item: TimelineItem }) {
  if (item.kind === 'evento') {
    return (
      <div className="flex items-center gap-2 py-1">
        <span className="h-px flex-1 bg-gray-100" />
        <span className="flex items-center gap-1 text-[11px] text-gray-400">
          <Clock size={11} />
          {item.authorName ? `${item.authorName} ` : ''}{eventLabel(item.type)}
          {item.to ? ` para ${item.to}` : ''}
          {item.note ? ` — ${item.note}` : ''}
          · {item.formatted.createdAt}
        </span>
        <span className="h-px flex-1 bg-gray-100" />
      </div>
    )
  }

  const mine = !item.fromClient
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[76%] rounded-lg px-3 py-2 text-sm ${
        item.isNote
          ? 'border border-amber-200 bg-amber-50 text-amber-900'
          : mine ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-800'
      }`}>
        {item.isNote && (
          <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide">
            <StickyNote size={11} /> Comentário interno
          </p>
        )}
        {item.message && <p className="whitespace-pre-wrap break-words">{item.message}</p>}

        {item.attachments.map((a) => (
          <div key={a.id} className="mt-2">
            {a.kind === 'IMAGEM' && a.url ? (
              // Abre em nova aba: o print costuma vir pequeno na bolha.
              <a href={a.url} target="_blank" rel="noreferrer" title="Abrir em tamanho real">
                <img src={a.url} alt={a.name ?? 'anexo'} className="max-h-56 rounded-md" />
              </a>
            ) : a.kind === 'AUDIO' && a.url ? (
              <audio controls preload="metadata" src={a.url} className="w-56 max-w-full" />
            ) : a.kind === 'VIDEO' && a.url ? (
              <video controls preload="metadata" src={a.url} className="max-h-56 rounded-md" />
            ) : (
              <a href={a.url ?? '#'} target="_blank" rel="noreferrer"
                 className="flex items-center gap-1 text-xs underline">
                <Paperclip size={12} /> {a.name ?? 'arquivo'}
              </a>
            )}
          </div>
        ))}

        <p className={`mt-1 text-[10px] ${item.isNote ? 'text-amber-700' : mine ? 'text-white/70' : 'text-gray-500'}`}>
          {item.authorName ? `${item.authorName} · ` : ''}{item.formatted.createdAt}
          {item.status && item.status !== 'FALHOU' ? ` · ${item.status.toLowerCase()}` : ''}
        </p>
        {item.status === 'FALHOU' && (
          <div role="alert" className="mt-2 flex items-start gap-1.5 rounded-md bg-white px-2 py-1.5 text-xs text-rose-700 ring-1 ring-rose-200">
            <AlertTriangle size={14} className="mt-px shrink-0" />
            <span>
              <strong className="font-semibold">Não entregue.</strong>{' '}
              {item.failed || 'O WhatsApp não entregou a mensagem.'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/** Ação da barra do compositor: ícone com rótulo no title e no leitor de tela. */
function IconButton({ label, active, onClick, children }: {
  label: string; active?: boolean; onClick: () => void; children: React.ReactNode
}) {
  return (
    <button
      type="button" onClick={onClick} title={label} aria-label={label} aria-pressed={active}
      className={`cursor-pointer inline-flex h-8 w-8 items-center justify-center rounded-md transition ${
        active ? 'bg-rose-50 text-rose-600' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  )
}

/** Tecla desenhada, para a dica de atalho não virar texto corrido. */
function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-b-2 border-gray-200 bg-white px-1 py-px text-[10px] font-medium text-gray-500">
      {children}
    </kbd>
  )
}

/** Anexo ainda não enviado: print aparece como miniatura, áudio com player. */
function PendingFile({ file, onRemove }: { file: File; onRemove: () => void }) {
  const preview = file.type.startsWith('image/') || file.type.startsWith('audio/')
  const url = useMemo(() => (preview ? URL.createObjectURL(file) : null), [file, preview])

  // Libera a memória do preview quando o anexo sai da lista.
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])

  return (
    <span className="relative inline-flex items-center gap-1 rounded-md bg-gray-100 p-1 pr-6 text-[11px] text-gray-600">
      {url && file.type.startsWith('image/') && <img src={url} alt={file.name} className="h-14 w-14 rounded object-cover" />}
      {url && file.type.startsWith('audio/') && <audio controls src={url} className="h-8 w-44" />}
      {!preview && <span className="px-1">{file.name}</span>}
      <button type="button" onClick={onRemove} aria-label="Remover anexo"
        className="cursor-pointer absolute right-1 top-1 rounded bg-white/90 p-0.5 text-gray-500 ring-1 ring-gray-200 transition hover:text-rose-600">
        <X size={11} />
      </button>
    </span>
  )
}

/** Segundos em m:ss — o contador da gravação. */
function formatSeconds(total: number): string {
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-sm text-gray-700">{value}</p>
    </div>
  )
}

function eventLabel(type: string): string {
  return {
    ATRIBUIU: 'assumiu o chamado',
    TRANSFERIU: 'transferiu',
    ENCERROU: 'encerrou o chamado',
    REABRIU: 'reabriu o chamado',
    ADIOU: 'adiou',
    VINCULOU: 'vinculou o contato',
  }[type] ?? type.toLowerCase()
}
